import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getCompanies, updateCompany, createCompany } from '../services/api';
import './CompanyManagement.css';

const NEW_COMPANY = { isNew: true };

function CompanyManagement({ embedded = false }) {
  const { t } = useTranslation();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', mandantennummer: '', anschrift: '', duns: '', steuernummer: '' });
  const [saving, setSaving] = useState(false);

  const isNew = editModal === NEW_COMPANY;

  const loadCompanies = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getCompanies();
      setCompanies(data.companies || []);
    } catch (err) {
      setError(err.message || t('admin.company_management.errors.load_companies_failed'));
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  const openNew = () => {
    setEditModal(NEW_COMPANY);
    setEditForm({ name: '', mandantennummer: '', anschrift: '', duns: '', steuernummer: '' });
  };

  const openEdit = (company) => {
    setEditModal(company);
    setEditForm({
      name: company.name || '',
      mandantennummer: company.mandantennummer || '',
      anschrift: company.anschrift || '',
      duns: company.duns || '',
      steuernummer: company.steuernummer || '',
    });
  };

  const closeEdit = () => {
    setEditModal(null);
  };

  const handleSave = async () => {
    if (isNew) {
      const mandantennummer = (editForm.mandantennummer || '').trim();
      const name = (editForm.name || '').trim();
      if (!name) {
        setError(t('admin.company_management.errors.name_required'));
        return;
      }
      if (!mandantennummer) {
        setError(t('admin.company_management.errors.mandantennummer_required'));
        return;
      }
      try {
        setSaving(true);
        setError('');
        await createCompany({
          name,
          mandantennummer,
          anschrift: editForm.anschrift || undefined,
          duns: editForm.duns || undefined,
          steuernummer: editForm.steuernummer || undefined,
        });
        await loadCompanies();
        closeEdit();
      } catch (err) {
        setError(err.message || t('admin.company_management.errors.create_failed'));
      } finally {
        setSaving(false);
      }
      return;
    }
    if (!editModal?.id) return;
    try {
      setSaving(true);
      setError('');
      await updateCompany(editModal.id, {
        name: editForm.name || undefined,
        anschrift: editForm.anschrift || undefined,
        duns: editForm.duns || undefined,
        steuernummer: editForm.steuernummer || undefined,
      });
      await loadCompanies();
      closeEdit();
    } catch (err) {
      setError(err.message || t('admin.company_management.errors.save_failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="company-management">
      {!embedded && <h2>{t('admin.company_management.title')}</h2>}
      {error && <div className="error-message">{error}</div>}
      {loading ? (
        <div className="loading">{t('admin.company_management.loading')}</div>
      ) : (
        <>
        <div className="company-toolbar">
          <button type="button" className="btn btn-primary" onClick={openNew}>
            {t('admin.company_management.buttons.new_company')}
          </button>
        </div>
        <table className="admin-table">
          <thead>
            <tr>
              <th className="col-id">{t('admin.company_management.table.id')}</th>
              <th>{t('admin.company_management.table.name')}</th>
              <th>{t('admin.company_management.table.mandantennummer')}</th>
              <th>{t('admin.company_management.table.anschrift')}</th>
              <th>{t('admin.company_management.table.duns')}</th>
              <th>{t('admin.company_management.table.steuernummer')}</th>
              <th>{t('admin.company_management.table.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {companies.length === 0 ? (
              <tr>
                <td colSpan="7" style={{ textAlign: 'center' }}>
                  {t('admin.company_management.empty')}
                </td>
              </tr>
            ) : (
              companies.map((c) => (
                <tr key={c.id}>
                  <td className="col-id">{c.id}</td>
                  <td>{c.name}</td>
                  <td>{c.mandantennummer}</td>
                  <td className="col-anschrift">{c.anschrift || '–'}</td>
                  <td>{c.duns || '–'}</td>
                  <td>{c.steuernummer || '–'}</td>
                  <td>
                    <button type="button" className="btn btn-secondary" onClick={() => openEdit(c)}>
                      {t('admin.common.buttons.edit')}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </>
      )}

      {editModal && (
        <div className="modal-overlay" onClick={closeEdit}>
          <div className="modal-content company-modal" onClick={(e) => e.stopPropagation()}>
            <h3>
              {isNew ? t('admin.company_management.modal.title_new') : t('admin.company_management.modal.title_edit')}
            </h3>
            <div className="modal-form">
              <div className="form-row">
                <label>{t('admin.company_management.fields.mandantennummer')}</label>
                {isNew ? (
                  <input
                    type="text"
                    value={editForm.mandantennummer}
                    onChange={(e) => setEditForm((f) => ({ ...f, mandantennummer: e.target.value }))}
                  />
                ) : (
                  <span className="readonly-value">{editModal.mandantennummer}</span>
                )}
              </div>
              <div className="form-row">
                <label>{t('admin.company_management.fields.name')}</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                />
              </div>
              <div className="form-row">
                <label>{t('admin.company_management.fields.anschrift')}</label>
                <textarea
                  value={editForm.anschrift}
                  onChange={(e) => setEditForm((f) => ({ ...f, anschrift: e.target.value }))}
                  rows={3}
                />
              </div>
              <div className="form-row">
                <label>{t('admin.company_management.fields.duns')}</label>
                <input
                  type="text"
                  value={editForm.duns}
                  onChange={(e) => setEditForm((f) => ({ ...f, duns: e.target.value }))}
                />
              </div>
              <div className="form-row">
                <label>{t('admin.company_management.fields.steuernummer')}</label>
                <input
                  type="text"
                  value={editForm.steuernummer}
                  onChange={(e) => setEditForm((f) => ({ ...f, steuernummer: e.target.value }))}
                />
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? t('admin.common.buttons.saving') : t('admin.common.buttons.save')}
              </button>
              <button type="button" className="btn btn-secondary" onClick={closeEdit}>
                {t('admin.common.buttons.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CompanyManagement;
